import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Heading,
  BodyText,
  Button,
  Card,
  MetricCard,
  BackLink,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import {
  ArrowRight as TransitionIcon,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { useFiling, useFilingEvents, useFilingChain, useTransitionFiling } from '../../hooks';
import { ABSENT, formatCount, formatDate, formatDateTime } from '../../utils/display-format';
import {
  CELL_CONTROL_CLASS,
  CodeBadge,
  FILING_STATUS_LABEL,
  FILING_STATUS_VARIANT,
  FILING_TYPE_LABEL,
  labelFor,
  UccReadFailure,
  type FilingStatus,
  type UccFiling,
} from './ucc-display';

type UccFilingEvent = components['schemas']['UccFilingEvent'];
type FilingChainResponse = components['schemas']['FilingChainResponse'];

/**
 * The transitions this page offers, keyed by the filing's current status.
 * Each target is one the ucc-filing state machine accepts from that status
 * (filing-lifecycle-state-machine.ts: draft -> submitted, active ->
 * terminated). The page offered `draft -> pending_submission` and
 * `filed -> terminated`; neither `pending_submission` nor `filed` is a
 * FilingStatus, so the buttons never showed or never succeeded.
 */
export const OFFERED_TRANSITIONS: Partial<Record<FilingStatus, { toStatus: FilingStatus; label: string }>> = {
  draft: { toStatus: 'submitted', label: 'Submit for Filing' },
  active: { toStatus: 'terminated', label: 'Terminate Filing' },
};

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  return Math.ceil((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

/** "Draft to Submitted" from an event's status pair, through the FilingStatus labels. */
export function eventTransitionLabel(event: Pick<UccFilingEvent, 'fromStatus' | 'toStatus'>): string {
  return `${labelFor(FILING_STATUS_LABEL, event.fromStatus)} to ${labelFor(FILING_STATUS_LABEL, event.toStatus)}`;
}

/** Every filing in the chain, root first, as GET /ucc-filings/{filingId}/chain returns them. */
export function chainFilings(chain: FilingChainResponse | undefined): UccFiling[] {
  if (!chain) return [];
  return [chain.rootFiling, ...chain.children];
}

function DetailField({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <BodyText variant="ui" className="text-xs text-tertiary">{label}</BodyText>
      <BodyText variant="ui">{children}</BodyText>
    </div>
  );
}

export function UCCFilingDetailPage() {
  const { filingId } = useParams<{ filingId: string }>();
  const userId = useAuthStore(s => s.user?.id) ?? '';

  const { data: filing, isLoading, error, refetch } = useFiling(filingId);
  const events = useFilingEvents(filingId);
  const chain = useFilingChain(filingId);
  const transitionFiling = useTransitionFiling();

  if (isLoading) {
    return (
      <div className="p-6">
        <BodyText variant="ui" className="text-tertiary">Loading filing details...</BodyText>
      </div>
    );
  }

  if (error || !filing) {
    return (
      <div>
        <BackLink href="/ucc/filings" className="mb-4">Back to Filings</BackLink>
        <UccReadFailure
          title="Could not load this filing"
          error={error ?? new Error('The filing service returned no filing.')}
          onRetry={() => refetch()}
          className=""
        />
      </div>
    );
  }

  const daysActive = daysSince(filing.filingDate);
  const daysToExpiration = daysUntil(filing.expirationDate);
  const eventRows = events.data?.data ?? [];
  const chainRows = chainFilings(chain.data);
  const offered = OFFERED_TRANSITIONS[filing.status];

  const handleTransition = async (toStatus: FilingStatus) => {
    if (!filingId || !userId) return;
    try {
      await transitionFiling.mutateAsync({ filingId, body: { toStatus, initiatedBy: userId } });
      refetch();
    } catch {
      // Rendered below from transitionFiling.error.
    }
  };

  return (
    <div>
      <BackLink href="/ucc/filings" className="mb-4">Back to Filings</BackLink>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <CodeBadge code={filing.status} labels={FILING_STATUS_LABEL} variants={FILING_STATUS_VARIANT} />
            <CodeBadge code={filing.filingType} labels={FILING_TYPE_LABEL} />
          </div>
          <BodyText variant="ui" className="text-tertiary">
            Jurisdiction: {filing.jurisdictionCode || ABSENT}
          </BodyText>
        </div>
        {offered && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={offered.toStatus === 'terminated' ? 'secondary' : 'primary'}
              onClick={() => handleTransition(offered.toStatus)}
              disabled={transitionFiling.isPending || !userId}
            >
              {offered.toStatus !== 'terminated' && <TransitionIcon className="mr-2" size={16} />}
              {offered.label}
            </Button>
          </div>
        )}
      </div>

      {transitionFiling.error && (
        <Alert type="error" title="The filing status did not change" className="mb-4">
          {transitionFiling.error.message}
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Days Active" value={daysActive !== null ? daysActive.toString() : ABSENT} />
        <MetricCard label="Days to Expiration" value={daysToExpiration !== null ? daysToExpiration.toString() : ABSENT} />
        <MetricCard label="Filing Type" value={labelFor(FILING_TYPE_LABEL, filing.filingType)} />
        <MetricCard label="Jurisdiction" value={filing.jurisdictionCode || ABSENT} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card className="p-6">
            <Heading level={2} size="label" className="font-sans mb-4">Filing Information</Heading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailField label="Filing Number">{filing.filingNumber || 'Not yet assigned'}</DetailField>
              <DetailField label="Filing Type">{labelFor(FILING_TYPE_LABEL, filing.filingType)}</DetailField>
              <DetailField label="Jurisdiction">{filing.jurisdictionCode || ABSENT}</DetailField>
              <DetailField label="Filed Date">{formatDate(filing.filingDate)}</DetailField>
              <DetailField label="Expiration Date">{formatDate(filing.expirationDate)}</DetailField>
              <DetailField label="Collateral Description" className="sm:col-span-2">
                {filing.collateralDescription || ABSENT}
              </DetailField>
            </div>
          </Card>

          <Card className="p-6">
            <Heading level={2} size="label" className="font-sans mb-4">Entities</Heading>
            <div className="space-y-3">
              <DetailField label="Secured Party ID">
                <span className="font-mono text-sm break-all">{filing.securedPartyId}</span>
              </DetailField>
              <DetailField label="Debtor ID">
                <span className="font-mono text-sm break-all">{filing.debtorId}</span>
              </DetailField>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <Heading level={2} size="label" className="font-sans mb-4">Event Timeline</Heading>
            {events.isLoading ? (
              <BodyText variant="ui" className="text-tertiary text-sm">Loading events...</BodyText>
            ) : events.error ? (
              <BodyText variant="ui" className="text-sm text-error">Could not be read: {events.error.message}</BodyText>
            ) : eventRows.length === 0 ? (
              <BodyText variant="ui" className="text-tertiary text-sm">No events recorded for this filing.</BodyText>
            ) : (
              <ol className="space-y-3">
                {eventRows.map((event) => (
                  <li key={event.eventId} className="pb-3 border-b border-recessed last:border-0">
                    <BodyText variant="ui" className="text-sm font-medium">{eventTransitionLabel(event)}</BodyText>
                    <BodyText variant="ui" className="text-xs text-tertiary">
                      {formatDateTime(event.eventTimestamp)}, by {event.initiatedBy}
                    </BodyText>
                  </li>
                ))}
              </ol>
            )}
            {!events.error && events.data && events.data.count > eventRows.length && (
              <BodyText variant="ui" className="text-xs text-tertiary mt-3">
                The first {formatCount(eventRows.length)} of {formatCount(events.data.count)} events are listed.
              </BodyText>
            )}
          </Card>

          <Card className="p-6">
            <Heading level={2} size="label" className="font-sans mb-4">Filing Chain</Heading>
            {chain.isLoading ? (
              <BodyText variant="ui" className="text-tertiary text-sm">Loading the filing chain...</BodyText>
            ) : chain.error ? (
              <BodyText variant="ui" className="text-sm text-error">Could not be read: {chain.error.message}</BodyText>
            ) : chainRows.length <= 1 ? (
              <BodyText variant="ui" className="text-tertiary text-sm">No amendments, continuations or terminations.</BodyText>
            ) : (
              <ul className="space-y-2">
                {chainRows.map((related) => {
                  const current = related.filingId === filing.filingId;
                  return (
                    <li
                      key={related.filingId}
                      className="flex items-center justify-between gap-3 p-3 rounded-sm border border-recessed"
                    >
                      <div>
                        {current ? (
                          <BodyText variant="ui" className="text-sm font-medium">
                            {related.filingNumber || 'Not yet assigned'} (this filing)
                          </BodyText>
                        ) : (
                          <Link
                            to={`/ucc/filings/${related.filingId}`}
                            className={`text-sm font-sans font-medium ${CELL_CONTROL_CLASS}`}
                          >
                            {related.filingNumber || 'Not yet assigned'}
                          </Link>
                        )}
                        <BodyText variant="ui" className="text-xs text-tertiary">
                          {labelFor(FILING_TYPE_LABEL, related.filingType)}
                        </BodyText>
                      </div>
                      <CodeBadge code={related.status} labels={FILING_STATUS_LABEL} variants={FILING_STATUS_VARIANT} />
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
